<template>
    <div
        class="table-detail"
        :class="{
            'module-mode': moduleMode,
            'table-mode': currentBoardView === 'table',
            'kanban-mode': currentBoardView === 'kanban',
            'calendar-mode': currentBoardView === 'calendar',
            'page-mode': !['table', 'kanban', 'calendar'].includes(currentBoardView),
            'has-active-tabs': hasActiveTabs,
            'active-tab-mode': activeTabMode === 'record',
        }"
        :data-active-tab-mode="activeTabMode"
    >
        <!-- Active Tabs Component -->
        <TableActiveTabs
            v-if="table && !disableBoardViewChange"
            :table="table"
            :active-tab-id="activeTabId"
            @tab-selected="onTabSelected"
        />

        <TableToolbar
            v-if="table && !disableToolbar"
            :fields="tableFields"
            :filters="filterConditions"
            :selectedRecords="selectedRecords"
            :table-id="tableId"
            :table="table"
            :hiddenFields="hiddenFields"
            :lockedFields="lockedFields"
            :sort="currentSort"
            @filter-updated="onFilterUpdated"
            @search="onSearch"
            @records-deleted="onRecordsDeleted"
            @records-duplicated="onRecordsDuplicated"
            @edit-records="onEditRecords"
            @show-hide-fields="openHideFieldsPanel"
            @clear-hidden-fields="showAllFields"
            @view-created="onViewCreated"
            @show-view="onShowView"
            @edit-view="$_.noop"
            @create-new-view="createNewView"
            @open-import-modal="openImportModal"
            :current-view="currentView"
            v-show="activeTabId === 'home'"
            @record-created="onRecordCreated"
            :total="total"
            :cursor="cursor"
            @change-cursor="changeCursor"
            :moduleMode="moduleMode"
            :predefinedValues="predefinedValues"
            @group-changed="onGroupChanged"
            :group-value="groupValue"
            :current-board-view="currentBoardView"
            @board-view-changed="onBoardViewChanged"
            :color-conditions="colorConditions"
            @update-color-conditions="onColorConditionsChanged"
            :search-query="searchQuery"
            :selected-module="selectedModule"
            @download-selected-records="downloadRecordsAsExcel(true)"
            @download-all-records="downloadRecordsAsExcel(false)"
            :default-linked-record-cache="defaultLinkedRecordCache"
            :custom-pages="customPages"
            @page-created="onPageCreated"
            @page-updated="onPageUpdated"
            @page-deleted="onPageDeleted"
            @view-deleted="onViewDeleted"
            @view-duplicated="onViewDuplicated"
            @view-updated="onViewUpdated"
            @toggle-zebra-stripes="onToggleZebraStripes"
            :zebra-stripes="zebraStripes"
            :records="records"
        >
            <a-button @click="openCreateViewModal">
                <a-icon type="plus" />
                {{ $t("tables.newView") }}
            </a-button>
        </TableToolbar>

        <div class="table-body">
            <!-- Eğer aktif sekme bir kayıt sekmesiyse RecordViewer göster -->
            <template v-if="activeTabId !== 'home' && activeTab">
                <RecordViewer
                    :key="activeTabId"
                    :table-id="tableId"
                    :record-id="activeTabId"
                    class="record-viewer-container"
                />
            </template>

            <!-- Home sekmesi veya hiç sekme yoksa normal tablo görünümü -->
            <template v-else-if="currentBoardView === 'table'">
                <TableHeaders
                    v-if="table"
                    :table="table"
                    :isSelectionModeActive="isSelectionModeActive"
                    :selectedRecords="selectedRecords"
                    :isAllSelected="isAllSelected"
                    @positions-changed="onPositionsChanged"
                    @refresh-table="fetchTable"
                    @toggle-select-all="toggleSelectAll"
                    :visibleFields="visibleFields"
                    @hide-field="hideField"
                    @sort-changed="onSortChanged"
                    :moduleMode="moduleMode"
                    :currentSort="currentSort"
                    :columnWidths="columnWidths"
                    @column-width-changed="onColumnWidthChanged"
                />
                <TableRecords
                    v-if="table"
                    :table="table"
                    :records="records"
                    :isSelectionModeActive="isSelectionModeActive"
                    :selectedRecords="selectedRecords"
                    @record-updated="recordUpdated"
                    @toggle-selection="toggleSelection"
                    @selection-updated="handleSelectionUpdated"
                    @record-selected="onRecordSelected"
                    :visibleFields="visibleFields"
                    :pickerMode="pickerMode"
                    :groupValue="groupValue"
                    :getRecordColor="getRecordColor"
                    :loading="loading"
                    :filters="filterConditions"
                    @update-filters="onFilterUpdated"
                    :moduleRelatedListMode="moduleRelatedListMode"
                    :zebraStripes="zebraStripes"
                    @record-opened-in-tab="onRecordOpenedInTab"
                    :columnWidths="columnWidths"
                />
            </template>

            <div v-else-if="currentBoardView === 'kanban'" class="kanban-placeholder h-100">
                <TableKanbanView
                    :table="table"
                    :records="records"
                    @record-updated="recordUpdated"
                    :selectedRecords="selectedRecords"
                    :isSelectionModeActive="isSelectionModeActive"
                    @toggle-selection="toggleSelection"
                    @board-view-changed="currentBoardView = $event"
                    :getRecordColor="getRecordColor"
                    :groupValue="groupValue"
                    v-if="table"
                />
            </div>

            <div v-else-if="currentBoardView === 'calendar'" class="calendar-placeholder h-100">
                <TableCalendarView
                    :records="records"
                    :table="table"
                    @record-updated="recordUpdated"
                    :selectedRecords="selectedRecords"
                    :isSelectionModeActive="isSelectionModeActive"
                    @toggle-selection="toggleSelection"
                    :getRecordColor="getRecordColor"
                    :groupValue="groupValue"
                    v-if="table"
                />
            </div>

            <TablePage
                v-else-if="currentBoardView && customPages.some((page) => page._id === currentBoardView)"
                :page="currentPage"
                :table="table"
                :upstream-filters="{ filters: $_.concat(implicitFilters, filterConditions), searchQuery: searchQuery }"
                @page-updated="onPageUpdated"
            />

            <div v-if="!table && loading" class="loading-overlay">
                <a-spin size="large" />
            </div>
        </div>

        <TableFooter
            v-if="
                ['table', 'kanban', 'calendar'].includes(currentBoardView) && activeTabId === 'home' && !disableFooter
            "
            :record-count="total"
            :table-id="tableId"
            :table="table"
            :disabled="loading || isCreatingRecord"
            :cursor="cursor"
            :fields="tableFields"
            :records="records"
            @change-cursor="changeCursor"
            @create-new-record="onCreateNewRecord"
            @open-import-modal="openImportModal"
            :moduleMode="moduleMode"
            @export-records="downloadRecordsAsExcel"
            @export-with-settings="onExportWithSettings"
            ref="footer"
            @field-added="onFieldAdded"
            @field-updated="onFieldUpdated"
            @field-deleted="onFieldDeleted"
            @filters-updated="onFiltersUpdated"
            @group-updated="onGroupUpdated"
            @hidden-fields-updated="onHiddenFieldsUpdated"
            @color-conditions-updated="onColorConditionsUpdated"
            @multi-record-created="onMultiRecordCreated"
            @export-selected-records="downloadRecordsAsExcel(true)"
            :selectedRecords="selectedRecords"
        />

        <Modal2
            v-if="showEditMultiRecordModal"
            @cancel="closeEditMultiRecordModal"
            :showFooter="false"
            :title="$t('tables.editMultipleRecords')"
            className="edit-multi-record-modal keydown-prevent-modal"
        >
            <EditMultiRecord
                :table="table"
                :fields="tableFields"
                :selectedRecords="selectedRecords"
                @records-updated="onRecordsUpdated"
                @cancel="closeEditMultiRecordModal"
            />
        </Modal2>

        <!-- HideFields Bileşeni -->
        <HideFields
            v-if="showHideFieldsPanel"
            :fields="tableFields"
            :hiddenFields="hiddenFields"
            @close="closeHideFieldsPanel"
            @hide-field="hideField"
            @show-field="showField"
            @show-all-fields="showAllFields"
            @hide-all-fields="hideAllFields"
        />

        <Modal2
            v-if="showImportModal"
            :title="$t('tables.importRecords')"
            @cancel="closeImportModal"
            className="import-records-modal keydown-prevent-modal"
            :show-footer="false"
        >
            <ImportRecords
                :table="table"
                :fields="tableFields"
                @import-records="handleImport"
                @cancel="closeImportModal"
                :on-completed="closeImportModal"
                :on-cancelled="closeImportModal"
            />
        </Modal2>

        <Modal2 v-if="showCreateViewModal" :title="$t('tables.createNewView')" @cancel="closeCreateViewModal">
            <a-select v-model="newViewTemplate" style="width: 100%">
                <a-select-option value="chart">{{ $t("tables.chartTemplate") }}</a-select-option>
                <a-select-option value="blank">{{ $t("tables.blankTemplate") }}</a-select-option>
                <a-select-option value="form">{{ $t("tables.formTemplate") }}</a-select-option>
                <a-select-option value="gantt">{{ $t("tables.ganttTemplate") }}</a-select-option>
            </a-select>

            <template slot="footer">
                <a-button key="back" @click="closeCreateViewModal">
                    {{ $t("common.cancel") }}
                </a-button>
                <a-button key="submit" type="primary" @click="createNewView">
                    {{ $t("common.create") }}
                </a-button>
            </template>
        </Modal2>
    </div>
</template>
<script>
import Vue from "vue";
import TableToolbar from "./TableToolbar.vue";
import TableHeaders from "./TableHeaders.vue";
import TableRecords from "./TableRecords.vue";
import TableFooter from "./TableFooter.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import EditMultiRecord from "./components/EditMultiRecord.vue";
import { TABLE_API } from "../../../../api/table";
import HideFields from "./components/HideFields.vue";
import ImportRecords from "./components/ImportRecords.vue";
import TableKanbanView from "./kanban/TableKanbanView.vue";
import TableCalendarView from "./calendar/TableCalendarView.vue";
import TablePage from "./widgets/TablePage.vue";
import TableActiveTabs from "./components/TableActiveTabs.vue";
import RecordViewer from "./RecordViewer.vue";
import _ from "lodash";
import log2 from "../../../../utils/log2";
import moment from "moment";
import { expertToExcel } from "../../../../utils/utils";
import { mapState, mapGetters, mapActions } from "vuex";
import { socketMessageStream } from "../../../../utils/socketStream";
import { checkTablePermission, getUserDepartments, canAccessTable } from "../../../../utils/TablePermissionUtils";
import { TABLE_PERMISSION_SCOPES } from "../../../../utils/TablePermissionScopes";

const log = log2("TableDetail");
export default Vue.extend({
    name: "TableDetail",
    components: {
        TableFooter,
        TableRecords,
        TableHeaders,
        TableToolbar,
        Modal2,
        EditMultiRecord,
        HideFields,
        ImportRecords,
        TableKanbanView,
        TableCalendarView,
        TablePage,
        TableActiveTabs,
        RecordViewer,
    },
    props: {
        defaultTableId: {
            type: String,
        },
        defaultRecords: {
            type: Array,
        },
        moduleMode: {
            type: Boolean,
            default: false,
        },
        selectedModule: {
            type: Object,
            default: null,
        },
        predefinedValues: {
            type: Object,
        },
        pickerMode: {
            type: Boolean,
            default: false,
        },
        pageMode: {
            type: Boolean,
            default: false,
        },
        defaultLinkedRecordCache: {
            type: Object,
        },
        implicitFilters: {
            type: Array,
            default: () => [],
        },
        moduleRelatedListMode: {
            type: Boolean,
            default: false,
        },
        defaultBoardViewMode: {
            type: String,
            default: "table",
        },
        defaultBoardViewId: {
            type: String,
            default: null,
        },
        disableBoardViewChange: {
            type: Boolean,
            default: false,
        },
        disableToolbar: {
            type: Boolean,
            default: false,
        },
        disableFooter: {
            type: Boolean,
            default: false,
        },
        liteListMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            table: null,
            records: this.defaultRecords || [],
            total: 0,
            loading: true,
            isCreatingRecord: false,
            selectedRecords: [],
            filterConditions: [], // Filtre koşullarını burada tutuyoruz
            searchQuery: "",
            showEditMultiRecordModal: false,
            hiddenFields: [],
            showHideFieldsPanel: false,
            lockedFields: [],
            currentSort: {},
            currentView: null,
            showImportModal: false,
            cursor: {
                page: 1,
                perPage: 100,
            },
            groupValue: {
                fieldId: null,
                direction: "asc",
                showEmptyGroups: false,
            },
            colorConditions: [],
            currentBoardView: "table",
            socketSubscription: null,
            showCreateViewModal: false,
            newViewTemplate: null,
            currentBoardViewId: null,
            customPages: [],
            currentPageId: null,
            currentPage: null,
            zebraStripes: true, // Zebra mod için yeni değişken
            activeTabId: "home", // Varsayılan olarak home sekmesi aktif
            columnWidths: {}, // Kolon genişlikleri için yeni state
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
            tables: (state) => state.table.tables,
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
        }),
        ...mapGetters("table", ["getActiveTabsByTableId"]),
        userDepartments() {
            return getUserDepartments(this.user);
        },
        tableId() {
            return this.defaultTableId || this.$route.params.id;
        },
        isAllSelected() {
            return this.selectedRecords.length === this.records.length;
        },
        isSelectionModeActive() {
            return this.selectedRecords.length > 0;
        },
        tableFields() {
            return this.table ? this.table.fields : [];
        },
        visibleFields() {
            return this.tableFields.filter((field) => !this.hiddenFields.includes(field._id));
        },
        activeTabs() {
            return this.table ? this.getActiveTabsByTableId(this.table._id) : [];
        },
        activeTab() {
            return this.activeTabs.find((tab) => tab.recordId === this.activeTabId);
        },
        hasActiveTabs() {
            return this.activeTabs.length > 0;
        },
        activeTabMode() {
            return this.activeTabId !== "home" ? "record" : "home";
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        ...mapActions("table", ["addActiveTab"]),
        checkPermission(scope) {
            return checkTablePermission(this.table, scope, this.user, this.adminUser, this.userDepartments);
        },
        onBoardViewChanged(view) {
            console.log("onBoardViewChanged", view);
            this.currentBoardView = view;
        },
        initializeSocketListener() {
            this.socketSubscription = socketMessageStream.subscribe((message) => {
                if (message.cmd === `table_record_updated`) {
                    console.log("table_record_updated", message.data);
                    const { tableId, recordId, updatedFields } = message.data;

                    // Eğer mesaj bu tabloya ait değilse işlemi ignore et
                    if (tableId !== this.tableId) return;

                    this.records = this.records.map((record) => {
                        if (record._id === recordId) {
                            Object.keys(updatedFields).forEach((key) => {
                                record.data[key] = updatedFields[key];
                            });
                        }
                        return record;
                    });

                    this.records = _.cloneDeep(this.records);
                    log("[TableDetail] Record updated via socket:", {
                        recordId,
                        updatedFields,
                    });
                }
            });
        },
        onExportWithSettings(exportData) {
            // TableFooter'dan gelen export isteğini TableToolbar'daki metodlara yönlendir
            const toolbar = this.$children.find((child) => child.$options.name === "TableToolbar");
            if (toolbar) {
                toolbar.exportMode = exportData.mode;
                toolbar.onExportSettingsStart(exportData.settings);
            }
        },
        async downloadRecordsAsExcel(onlySelected = false) {
            if (!this.checkPermission(TABLE_PERMISSION_SCOPES.EXPORT)) {
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            
            try {
                const records = onlySelected
                    ? this.selectedRecords.map((id) => this.records.find((r) => r._id === id))
                    : this.records;

                console.log("records", records);

                // Verileri Excel formatına dönüştür
                const excelData = records.map((record) => {
                    const rowData = {};
                    const maxChar = 32767;
                    const format = (str = "") => {
                        if (str.length > maxChar) {
                            return str.substring(0, maxChar - 3) + "...";
                        }
                        return str;
                    };
                    this.visibleFields.forEach((field) => {
                        // Alan adını kolon başlığı olarak kullan
                        if (["string", "number", "date", "boolean"].includes(field.valueType)) {
                            rowData[field.name] = format(String(record.data[field._id]));
                        } else if (field.valueType === "array") {
                            let arr = record.data[field._id];
                            arr = Array.isArray(arr) ? arr : [arr];
                            arr = arr.filter((item) => item !== null && item !== undefined);
                            rowData[field.name] = arr.join(", ");
                        } else if (field.valueType === "json") {
                            rowData[field.name] = format(JSON.stringify(record.data[field._id]));
                        } else if (field.valueType === "asset") {
                            rowData[field.name] = record.data[field._id]?.cdnUrl;
                        } else if (field.valueType === "objectId") {
                            if (field.fieldType === "user") {
                                rowData[field.name] = record.data[field._id]?.name;
                            } else if (field.fieldType === "ticket") {
                                rowData[field.name] = record.data[field._id]?.subject;
                            } else if (field.fieldType === "event") {
                                rowData[field.name] = record.data[field._id]?.title;
                            } else if (field.fieldType === "task") {
                                rowData[field.name] = record.data[field._id]?.title;
                            } else if (field.fieldType === "conversation") {
                                rowData[field.name] = record.data[field._id]?._id;
                            } else if (field.fieldType === "contact") {
                                rowData[field.name] = record.data[field._id]?.fullname;
                                rowData[`${field.name} / Whatsapp Number`] = record.data[field._id]?.whatsappNumber;
                                rowData[`${field.name} / Email`] = record.data[field._id]?.email;
                                rowData[`${field.name} / Phone`] = record.data[field._id]?.phone;
                                rowData[`${field.name} / Platform`] = record.data[field._id]?.platform;
                                const channelName = record.data[field._id]?.channel
                                    ? this.channels.find((c) => c._id === record.data[field._id]?.channel)?.name ||
                                      "Silinmiş Kanal"
                                    : "Web";
                                rowData[`${field.name} / Channel`] = channelName;
                            } else {
                                rowData[field.name] = JSON.stringify(record.data[field._id]);
                            }
                        } else if (field.valueType === "linked-record") {
                            const remoteTable = this.tables.find((t) => t._id === field.options.linkedRecord.table);
                            if (!remoteTable) rowData[field.name] = "Tablo Bulunamadı";
                            else {
                                const labelField = field.options.linkedRecord.labelField;
                                const remoteField = remoteTable.fields.find((f) => f._id === labelField);
                                if (!remoteField) rowData[field.name] = "Alan Bulunamadı";
                                else {
                                    const isContactField = remoteField.fieldType === "contact";
                                    if (isContactField) {
                                        const contact = _.get(record, `data.${labelField}`, undefined);
                                        return contact ? contact.fullname : "İsimsiz Kişi";
                                    } else if (remoteField.fieldType === "user") {
                                        const user = _.get(record, `data.${labelField}`, undefined);
                                        return user ? user.name : "İsimsiz Kullanıcı";
                                    } else if (remoteField.fieldType === "conversation") {
                                        return "Chat";
                                    } else {
                                        return _.get(record, `data.${labelField}`, "Bağlı Kayıt");
                                    }
                                    rowData[field.name] = record.data[field._id].name;
                                }
                            }
                        } else {
                            rowData[field.name] = record.data[field._id];
                        }
                    });
                    return rowData;
                });

                // Excel dosyasını indir
                expertToExcel(excelData, `records.xlsx`, "records");

                this.$message.success(this.$t("Excel dosyası başarıyla oluşturuldu"));
                
                // Activity log
                try {
                    console.log('TableDetail - Activity log başlatılıyor', {
                        activityType: 'TABLE_DATA_DOWNLOADED',
                        tableId: this.tableId,
                        tableName: this.table ? this.table.name : '',
                        recordCount: excelData.length
                    });
                    
                    const result = await this.$store.dispatch('activity/logDownloadActivity', {
                        activityType: 'TABLE_DATA_DOWNLOADED',
                        data: {
                            tableId: this.tableId,
                            tableName: this.table ? this.table.name : '',
                            recordCount: excelData.length,
                            format: 'excel',
                            onlySelected: onlySelected
                        }
                    });
                    
                    console.log('TableDetail - Activity log sonucu:', result);
                    
                    if (!result || !result.success) {
                        console.error('TableDetail - Activity log başarısız:', result);
                    }
                } catch (error) {
                    console.error('TableDetail - Activity log error:', error);
                }
            } catch (error) {
                console.error("Excel indirme hatası:", error);
                this.$message.error(this.$t("Excel dosyası oluşturulurken bir hata oluştu"));
            }
        },
        sleep(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        async fetchTable(onlyRecords = false) {
            const tableFetchActive = !this.table || !onlyRecords;

            try {
                const table = tableFetchActive ? await TABLE_API.getTableById(this.tableId) : this.table;
                this.table = table;
                
                // Tablo erişim kontrolü
                if (!canAccessTable(this.table, this.user, this.adminUser, this.userDepartments)) {
                    this.$message.error(this.$t("tables.permissions.noPermission"));
                    this.$router.push("/tables");
                    return;
                }
                
                if (this.moduleMode && !this.pickerMode && !this.pageMode) {
                    this.records = this.defaultRecords || [];
                    this.total = (this.defaultRecords || []).length;
                } else {
                    this.loading = true;
                    const records = await TABLE_API.listRecords(
                        this.tableId,
                        this.filterConditions,
                        this.currentSort,
                        this.cursor,
                        this.searchQuery
                    );
                    this.loading = false;
                    this.records = records.items;
                    this.total = records.total;
                }
            } catch (error) {
                console.error(this.$t("tables.errorPullTable"), error);
                this.$message.error(this.$t("tables.errorPullTable"));
            } finally {
                if (this.loading) {
                    this.loading = false;
                }
            }
        },
        onRecordSelected(record) {
            this.$emit("record-selected", record);
        },
        async onCreateNewRecord() {
            if (!this.checkPermission(TABLE_PERMISSION_SCOPES.RECORD_CREATE)) {
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            
            this.isCreatingRecord = true;
            try {
                const newRecord = await TABLE_API.createEmptyRecord(this.tableId);
                this.records.push(newRecord);
                this.total = this.total + 1;
                // Baarılı mesajını göster
                this.$message.success(this.$t("tables.newRecordSucs"));
            } catch (error) {
                console.error(this.$t("tables.failedNewRecord"), error);
                // Hata mesajını göster
                this.$message.error(this.$t("tables.newRecordCreateError"));
            }
            this.isCreatingRecord = false;
        },
        onApplyFilter(filterConditions) {
            this.filterConditions = filterConditions;
            this.fetchTable(true);
        },
        recordUpdated(record) {
            console.log("TableDetail recordUpdated:", record._id);

            // Scroll pozisyonunu güncelleme öncesinde sakla
            let scrollState = null;
            const tableRecordsComponent = this.$children.find((child) => child.$options.name === "TableRecords");
            if (tableRecordsComponent && tableRecordsComponent.preserveScrollPosition) {
                scrollState = tableRecordsComponent.preserveScrollPosition();
            }

            // Sadece değişen kaydı güncelle, tüm diziyi yeniden oluşturma
            const index = this.records.findIndex((r) => r._id === record._id);
            if (index !== -1) {
                // splice kullanarak sadece ilgili kaydı değiştir
                //this.records.splice(index, 1, record);
                Vue.set(this.records, index, record);
            }

            // Güncelleme sonrası scroll pozisyonunu geri yükle
            if (scrollState && tableRecordsComponent && tableRecordsComponent.restoreScrollPosition) {
                this.$nextTick(() => {
                    tableRecordsComponent.restoreScrollPosition(scrollState);
                });
            }
        },
        toggleSelectAll(isSelected) {
            if (isSelected) {
                this.selectedRecords = this.records.map((record) => record._id);
            } else {
                this.selectedRecords = [];
            }
        },
        toggleSelection(recordId) {
            const index = this.selectedRecords.indexOf(recordId);
            if (index > -1) {
                this.selectedRecords.splice(index, 1);
            } else {
                this.selectedRecords.push(recordId);
            }
        },
        handleSelectionUpdated(selectedIds) {
            this.selectedRecords = selectedIds;
        },
        handleKeyDown(event) {
            const keydownPreventModalExists = document.querySelector(".keydown-prevent-modal");
            if (keydownPreventModalExists) {
                return;
            }
            if ((event.ctrlKey || event.metaKey) && event.key === "a") {
                const isInput =
                    document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "TEXTAREA";
                if (!isInput) {
                    event.preventDefault();
                    this.toggleSelectAll(!this.isAllSelected);
                }
            } else if ((event.metaKey || event.ctrlKey) && event.key === "r") {
                const isTableViewActivated = this.currentBoardView === "table";
                if (isTableViewActivated) {
                    event.preventDefault();
                    this.fetchTable(true);
                }
            }
        },
        onFilterUpdated(filters) {
            this.filterConditions = filters;
            this.cursor = {
                page: 1,
                perPage: this.cursor.perPage,
            };

            this.fetchTable(true);
        },
        onSearch(query) {
            this.searchQuery = query;
            this.cursor = {
                page: 1,
                perPage: this.cursor.perPage,
            };
            this.fetchTable(true);
        },
        async onRecordsDeleted(deletedRecordIds) {
            let scrollState = null;
            const prevPage = this.cursor.page;
            const tableRecordsComponent = this.$children.find((child) => child.$options.name === "TableRecords");
            if (tableRecordsComponent && tableRecordsComponent.preserveScrollPosition) {
                scrollState = tableRecordsComponent.preserveScrollPosition();
            }

            this.records = this.records.filter((record) => !deletedRecordIds.includes(record._id));
            this.selectedRecords = [];
            this.total = this.total - deletedRecordIds.length;

            const perPage = this.cursor.perPage;
            const maxPage = Math.max(1, Math.ceil((this.total || 0) / perPage));
            if (this.cursor.page > maxPage) {
                this.cursor = { page: maxPage, perPage: perPage };
            }

            await this.fetchTable(true);

            if (
                scrollState &&
                prevPage === this.cursor.page &&
                tableRecordsComponent &&
                tableRecordsComponent.restoreScrollPosition
            ) {
                this.$nextTick(() => {
                    tableRecordsComponent.restoreScrollPosition(scrollState);
                });
            }
        },
        async onRecordsDuplicated(duplicatedRecordIds) {
            this.selectedRecords = [];
            await this.sleep(500);
            await this.fetchTable(true);
            this.scrollToLastRecord();
        },
        scrollToLastRecord() {
            const lastRecord = this.records[this.records.length - 1];
            if (lastRecord) {
                const lastRecordElement = document.querySelector(`[data-record-id="${lastRecord._id}"]`);
                if (lastRecordElement) {
                    lastRecordElement.scrollIntoView({ behavior: "smooth" });
                }
            }
        },
        onEditRecords() {
            this.showEditMultiRecordModal = true;
        },
        closeEditMultiRecordModal() {
            this.showEditMultiRecordModal = false;
        },
        async onRecordsUpdated() {
            await this.fetchTable(true);
            this.selectedRecords = [];
            this.closeEditMultiRecordModal();
        },
        openHideFieldsPanel() {
            this.showHideFieldsPanel = true;
        },
        closeHideFieldsPanel() {
            this.showHideFieldsPanel = false;
        },
        hideField(fieldId) {
            if (!this.hiddenFields.includes(fieldId)) {
                this.hiddenFields.push(fieldId);
            }
        },
        showField(fieldId) {
            this.hiddenFields = this.hiddenFields.filter((id) => id !== fieldId);
        },
        showAllFields() {
            this.hiddenFields = [];
        },
        hideAllFields() {
            this.hiddenFields = this.tableFields.map((field) => field._id);
        },
        onViewCreated(newView) {
            if (this.table && this.table.views) {
                this.table.views.push(newView);
                this.table.totalViews = this.table.totalViews + 1;
            } else if (this.table) {
                this.$set(this.table, "views", [newView]);
                this.$set(this.table, "totalViews", 1);
            }
        },
        onShowView(view) {
            if (view) {
                this.currentView = view;
                this.filterConditions = view.filters || [];
                this.currentSort = view.sort || {};
                this.hiddenFields = view.hiddenFields || [];
                this.lockedFields = view.lockedFields || [];
                this.searchQuery = view.search || "";

                if (view.groupValue) {
                    this.groupValue = view.groupValue;
                } else {
                    this.groupValue = { fieldId: null, direction: "asc", showEmptyGroups: false };
                }
                this.colorConditions = view.colorConditions || [];
                this.cursor = view.cursor || { page: 1, perPage: 100 };
            } else {
                this.currentView = null;
                this.filterConditions = [];
                this.currentSort = {};
                this.hiddenFields = [];
                this.lockedFields = [];
                this.searchQuery = "";
                this.groupValue = { fieldId: null, direction: "asc", showEmptyGroups: false };
                this.colorConditions = [];
                this.cursor = { page: 1, perPage: 100 };
            }
            this.fetchTable(true);
        },
        openImportModal() {
            if (!this.checkPermission(TABLE_PERMISSION_SCOPES.BULK_IMPORT)) {
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            this.showImportModal = true;
        },
        closeImportModal() {
            this.showImportModal = false;
        },
        async handleImport(values) {
            if (!this.checkPermission(TABLE_PERMISSION_SCOPES.BULK_IMPORT)) {
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            
            try {
                this.$message.info(this.$t("tables.importHasStarted"));
                await TABLE_API.createRecordMulti(this.tableId, values);
            } catch (error) {
                console.error("İçeri aktarım hatası:", error);
                this.$message.error(this.$t("tables.anErrorImport"));
            } finally {
                this.closeImportModal();
            }
        },
        onPositionsChanged(newPositions) {
            this.table.fields.forEach((field) => {
                field.position = newPositions[field._id];
            });
            this.table.fields.sort((a, b) => a.position - b.position);
            this.table = _.cloneDeep(this.table);
        },
        async onRecordCreated(newRecord) {
            await this.sleep(500);
            this.fetchTable(true);
            if (this.moduleMode) {
                this.$emit("record-created", newRecord);
            }
        },
        changeCursor(newCursor) {
            this.cursor = newCursor;
            this.fetchTable(true);
        },
        onSortChanged(sortInfo) {
            log("onSortChanged", sortInfo);
            this.currentSort = sortInfo;
            this.fetchTable(true);
        },
        onGroupChanged(newGroup) {
            this.groupValue = newGroup;
        },
        getRecordColor(record) {
            for (const condition of this.colorConditions) {
                const fieldValue = record.data[condition.fieldId];
                if (this.evaluateCondition(condition, fieldValue)) {
                    return condition.color;
                }
            }
            return null;
        },
        evaluateCondition(condition, fieldValue) {
            switch (condition.operator) {
                case "contains":
                    return fieldValue.includes(condition.value);
                case "equal":
                    return fieldValue === condition.value;
                case "not_equal":
                    return fieldValue !== condition.value;
                case "starts_with":
                    return fieldValue.startsWith(condition.value);
                case "ends_with":
                    return fieldValue.endsWith(condition.value);
                case "greater_than":
                    return fieldValue > condition.value;
                case "less_than":
                    return fieldValue < condition.value;
                case "before":
                    return moment(fieldValue).isBefore(condition.value);
                case "after":
                    return moment(fieldValue).isAfter(condition.value);
                default:
                    return false;
            }
        },
        onColorConditionsChanged(newColorConditions) {
            console.log("[conditioning color] onColorConditionsChanged", newColorConditions);
            this.colorConditions = newColorConditions;
            this.colorConditions = _.cloneDeep(this.colorConditions);
        },
        openCreateViewModal() {
            this.showCreateViewModal = true;
        },
        closeCreateViewModal() {
            this.showCreateViewModal = false;
            this.newViewTemplate = null;
        },
        async createNewView(template) {
            if (!this.checkPermission(TABLE_PERMISSION_SCOPES.PAGE_CREATE)) {
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            
            try {
                const newPage = await TABLE_API.createTablePage(this.tableId, "New Page", "", template);
                this.customPages.push(newPage);
                this.currentBoardView = newPage._id;
                this.currentPageId = newPage._id;
            } catch (error) {
                console.error("Failed to create new page", error);
                this.$message.error("Failed to create new page");
            }
        },
        async fetchCustomPages() {
            try {
                const pages = await TABLE_API.listTablePages(this.tableId);
                this.customPages = pages;
            } catch (error) {
                console.error("Failed to fetch custom pages", error);
                this.$message.error("Failed to fetch custom pages");
            }
        },
        onPageUpdated(updatedPage) {
            const pageIndex = this.customPages.findIndex((p) => p._id === updatedPage._id);
            if (pageIndex !== -1) {
                this.customPages.splice(pageIndex, 1, updatedPage);
                this.currentPage = updatedPage;
            }
        },
        onPageCreated(newPage) {
            this.customPages = [...this.customPages, newPage];
        },
        onPageDeleted(deletedPageId) {
            this.customPages = this.customPages.filter((p) => p._id !== deletedPageId);
            if (this.currentPage && this.currentPage._id === deletedPageId) {
                this.currentBoardView = "table";
            }
        },
        onViewDeleted(deletedViewId) {
            if (this.table && this.table.views) {
                this.table.views = this.table.views.filter((view) => view._id !== deletedViewId);
                this.table.totalViews = this.table.totalViews - 1;
            }
        },
        onViewDuplicated(newView) {
            if (this.table && this.table.views) {
                this.table.views.push(newView);
                this.table.totalViews = this.table.totalViews + 1;
            }
        },
        onViewUpdated(updatedView) {
            if (this.table && this.table.views) {
                const viewIndex = this.table.views.findIndex((view) => view._id === updatedView._id);
                if (viewIndex !== -1) {
                    this.table.views.splice(viewIndex, 1, updatedView);

                    // Eğer şu anda bu görünüm seçiliyse, currentView'ı da güncelle
                    if (this.currentView && this.currentView._id === updatedView._id) {
                        this.currentView = updatedView;
                    }
                }
            }
        },
        onToggleZebraStripes() {
            this.zebraStripes = !this.zebraStripes;
            console.log("Zebra stripes toggled to ", this.zebraStripes);
        },
        onTabSelected(tabId) {
            this.activeTabId = tabId;

            // Eğer bir kayıt sekmesi seçildiyse ama o kayıt artık açık sekmeler arasında yoksa home'a dön
            if (tabId !== "home" && !this.activeTabs.find((tab) => tab.recordId === tabId)) {
                this.activeTabId = "home";
            }
        },
        onRecordOpenedInTab(recordData) {
            // Kayıt bilgilerini store'a ekle
            const identifierField = this.table.fields.find((field) => field.isIdentifierField);
            let title = `${this.table.name} - ${this.$t("modules.recordViewer")} (${recordData.recordId})`;

            if (identifierField) {
                const isLinkedRecord = identifierField.fieldType === "linked-record";
                if (isLinkedRecord) {
                    const remoteTable = this.tables.find((t) => t._id === identifierField.options.linkedRecord.table);
                    if (remoteTable) {
                        const labelField = identifierField.options.linkedRecord.labelField;
                        const remoteField = remoteTable.fields.find((f) => f._id === labelField);
                        if (remoteField) {
                            const recordFieldData = recordData.data[identifierField._id];
                            if (remoteField.fieldType === "contact") {
                                const contact = this.$_.get(recordFieldData, `data.${labelField}`, undefined);
                                title = contact ? contact.fullname : "İsimsiz Kişi";
                            } else if (remoteField.fieldType === "user") {
                                const user = this.$_.get(recordFieldData, `data.${labelField}`, undefined);
                                title = user ? user.name : "İsimsiz Kullanıcı";
                            } else if (remoteField.fieldType === "conversation") {
                                title = "Chat";
                            } else {
                                title = this.$_.get(recordFieldData, `data.${labelField}`, "Bağlı Kayıt");
                            }
                            title = `${title}`;
                        } else {
                            title = `Bağlı Kayıt`;
                        }
                    } else {
                        title = `${this.table.name} - Tablo Bulunamadı`;
                    }
                } else {
                    const identifierValue = recordData.data[identifierField._id];
                    if (identifierValue) {
                        // Contact field ise
                        if (identifierField.fieldType === "contact") {
                            title = identifierValue.fullname || "İsimsiz Kişi";
                        }
                        // User field ise
                        else if (identifierField.fieldType === "user") {
                            title = identifierValue.name || "İsimsiz Kullanıcı";
                        }
                        // Conversation field ise
                        else if (identifierField.fieldType === "conversation") {
                            title = "Chat";
                        }
                        // Diğer durumlar için string'e çevir
                        else {
                            title = identifierValue.toString();
                        }
                        title = `${title}`;
                    } else {
                        title = `${this.table.name} - Kayıt ${recordData.recordId}`;
                    }
                }
            }

            this.addActiveTab({
                tableId: this.table._id,
                recordId: recordData._id,
                title: title,
                data: recordData,
            });

            // // Bu sekmeyi aktif yap
            // this.activeTabId = recordData._id;
        },
        onFieldAdded(field) {
            this.fetchTable(false);
            this.$message.success(`Alan eklendi: ${field.name}`);
        },
        onFieldUpdated(field) {
            this.fetchTable(false);
            this.$message.success(`Alan güncellendi: ${field.name}`);
        },
        onFieldDeleted(fieldId) {
            this.fetchTable(false);
            this.$message.success(`Alan silindi`);
        },
        onFiltersUpdated(filters) {
            this.onFilterUpdated(filters);
        },
        onGroupUpdated(groupValue) {
            // Grup değerini güncelle
            this.groupValue = groupValue;
            this.$message.success(`Gruplandırma uygulandı`);

            // Gruplandırmayı uygula ve kayıtları güncelle
            this.fetchTable();
        },
        onHiddenFieldsUpdated(hiddenFields) {
            // Gizli alanları güncelle
            this.hiddenFields = hiddenFields;
            this.$message.success(`Alan gizleme uygulandı (${hiddenFields.length} alan)`);

            // Kayıtları güncelle
            this.fetchTable();
        },
        onColorConditionsUpdated(colorConditions) {
            // Renk koşullarını güncelle
            this.colorConditions = colorConditions;
            this.$message.success(`Renklendirme kuralları uygulandı (${colorConditions.length} kural)`);

            // Renklendirme kurallarını uygula ve kayıtları güncelle
            this.fetchTable();
        },
        onMultiRecordCreated() {
            log("onMultiRecordCreated");
            this.fetchTable(true);
        },
        // Kolon genişlik yönetimi için yeni metodlar
        loadColumnWidths() {
            if (!this.tableId) return;

            try {
                const savedWidths = localStorage.getItem("table_column_widths");
                if (savedWidths) {
                    const allWidths = JSON.parse(savedWidths);
                    this.columnWidths = allWidths[this.tableId] || {};
                }
            } catch (error) {
                console.error("Failed to load column widths:", error);
                this.columnWidths = {};
            }
        },

        saveColumnWidths() {
            if (!this.tableId) return;

            try {
                const savedWidths = localStorage.getItem("table_column_widths");
                const allWidths = savedWidths ? JSON.parse(savedWidths) : {};
                allWidths[this.tableId] = this.columnWidths;
                localStorage.setItem("table_column_widths", JSON.stringify(allWidths));
            } catch (error) {
                console.error("Failed to save column widths:", error);
            }
        },

        onColumnWidthChanged(fieldId, width) {
            this.columnWidths = {
                ...this.columnWidths,
                [fieldId]: Math.max(100, width), // Minimum 100px
            };
            this.saveColumnWidths();
        },

        getColumnWidth(fieldId) {
            // Record ID kolonu için daha küçük varsayılan genişlik
            if (fieldId === "record-id") {
                return this.columnWidths[fieldId] || 100;
            }
            return this.columnWidths[fieldId] || 245; // Varsayılan genişlik 245px
        },
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyDown);
    },
    created() {
        
        this.initializeSocketListener();
        this.loadColumnWidths(); // Kolon genişliklerini yükle
        if (!this.disableBoardViewChange) {
            this.fetchCustomPages().then(() => {
                // URL'den pageId'yi kontrol et
                const pageId = this.$route.query.pageId;
                if (pageId) {
                    // pageId varsa ve geçerli bir sayfa ise o sayfayı aç
                    const pageExists = this.customPages.some((page) => page._id === pageId);
                    if (pageExists) {
                        this.currentBoardView = pageId;
                    }
                }
            });
        }
        if(this.liteListMode){
            this.currentBoardView = this.defaultBoardViewMode;  
            this.currentBoardViewId = this.defaultBoardViewId;
            this.filterConditions = this.implicitFilters;
        }
        this.fetchTable();
    },
    updated() {
        log("updated");
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeyDown);

        // Socket subscription'ı temizle
        if (this.socketSubscription) {
            this.socketSubscription.unsubscribe();
        }
    },
    watch: {
        defaultTableId(newVal) {
            this.fetchTable();
        },
        defaultRecords: {
            deep: true,
            handler(newVal, oldVal) {
                if (Array.isArray(newVal)) {
                    this.records = newVal;
                    const deletedCount = oldVal.length - newVal.length;
                    this.total = this.total - deletedCount;
                }
            },
        },
        predefinedValues: {
            deep: true,
            handler(newVal) {
                if (Object.keys(newVal).length > 0) {
                    this.fetchTable();
                }
            },
        },
        currentBoardView: {
            handler(newValue) {
                if (newValue && this.customPages.some((page) => page._id === newValue)) {
                    this.currentPage = this.customPages.find((page) => page._id === newValue);
                } else {
                    this.currentPage = null;
                }
            },
            immediate: true,
        },
        "$route.query.pageId": {
            handler(newPageId) {
                if (newPageId) {
                    // pageId varsa ve geçerli bir sayfa ise o sayfayı aç
                    const pageExists = this.customPages.some((page) => page._id === newPageId);
                    if (pageExists && this.currentBoardView !== newPageId) {
                        this.currentBoardView = newPageId;
                    }
                } else if (this.customPages.some((page) => page._id === this.currentBoardView)) {
                    // Eğer şu an özel bir sayfadaysak ve pageId kaldırıldıysa table görünümüne geç
                    this.currentBoardView = "table";
                }
            },
        },
    },
});
</script>
<style scoped lang="scss">
$footer-height: 40px;
$tabs-height: 32px;
$toolbar-height: 70px;

.table-body {
    height: calc(100vh - 194px);
    max-height: calc(100vh - 194px);
    overflow: auto;
    width: 100%;

    &::-webkit-scrollbar {
        width: 12px;
        height: 12px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 6px;
        border: 2px solid #f1f1f1;

        &:hover {
            background: #555;
        }
    }

    /* Yatay ve dikey scrollbar'ların kesişim noktası için stil */
    &::-webkit-scrollbar-corner {
        background: #f1f1f1;
    }
}

.table-detail {
    &.page-mode {
        .table-body {
            height: calc(100vh - 194px + $footer-height - 1px) !important;
            max-height: calc(100vh - 194px + $footer-height - 1px) !important;
            overflow-y: hidden !important;
        }
    }

    &.module-mode {
        .table-body {
            height: calc(100vh - 206px);
            max-height: calc(100vh - 206px);
        }
    }

    // Active tabs durumunda
    &.has-active-tabs {
        .table-body {
            height: calc(100vh - 194px - #{$tabs-height});
            max-height: calc(100vh - 194px - #{$tabs-height});
        }

        &.page-mode .table-body {
            height: calc(100vh - 194px + $footer-height - 1px - #{$tabs-height}) !important;
            max-height: calc(100vh - 194px + $footer-height - 1px - #{$tabs-height}) !important;
        }

        &.module-mode .table-body {
            height: calc(100vh - 206px - #{$tabs-height});
            max-height: calc(100vh - 206px - #{$tabs-height});
        }
    }

    // Record tab aktifken (toolbar gizli) daha fazla alan
    &[data-active-tab-mode="record"] {
        .table-body {
            height: calc(100vh - 194px - #{$tabs-height} + #{$toolbar-height}) !important;
            max-height: calc(100vh - 194px - #{$tabs-height} + #{$toolbar-height}) !important;
        }

        &.module-mode .table-body {
            height: calc(100vh - 206px - #{$tabs-height} + #{$toolbar-height}) !important;
            max-height: calc(100vh - 206px - #{$tabs-height} + #{$toolbar-height}) !important;
        }
    }
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 1000;
}

.record-viewer-container {
    height: 100%;
    overflow-y: auto;
}
</style>
<style lang="scss">
.import-records-modal {
    .ant-modal {
        width: 1300px !important;
    }
}

.table-detail {
    .supsis-button {
        font-size: 14px;
        padding: 5px 5px !important;
        background-color: #dfe2e9;
    }

    &.module-mode {
        .table-body {
            height: calc(100vh - 206px);
            max-height: calc(100vh - 206px);
        }
    }
}
</style>
